/**
 * @file TranslationUnavailableNoticeView.tsx
 * @description The non-blocking notice shown when a scout report could not be
 * translated (SE-6510 R4a).
 * @module reports
 */

import { RotateCcwIcon, TriangleAlertIcon } from "lucide-react-native";
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/**
 * Props for the TranslationUnavailableNoticeView component
 */
interface Props {
  /** The catalog message stating the translation is unavailable */
  message: string;
  /** The catalog label for the retry affordance */
  retryLabel: string;
  /** Called when the viewer asks to try the translation again */
  onRetry: () => void;
  /** Whether a retry is already in flight, which disables the affordance */
  isRetrying: boolean;
}

/**
 * A non-blocking notice that the translation is unavailable, plus a retry.
 *
 * It sits above the report body rather than replacing it: the whole point of
 * R4a is that the already-fetched original stays readable, so this must never
 * be the only thing on screen. Both strings arrive already resolved from the
 * catalog, and neither is ever the provider's error text — the underlying
 * failure stays in the console.
 * @param root0 - Component props
 * @param root0.message - The catalog message
 * @param root0.retryLabel - The catalog retry label
 * @param root0.onRetry - Called when the viewer asks to try again
 * @param root0.isRetrying - Whether a retry is already in flight
 */
const TranslationUnavailableNoticeView = ({
  message,
  retryLabel,
  onRetry,
  isRetrying,
}: Props) => (
  <Box
    testID="scout-report-translation-unavailable"
    accessibilityRole="alert"
    className="rounded-sm bg-status-warning-surface px-3 py-2"
  >
    <Stack direction="horizontal" className="items-center gap-2">
      <Icon as={TriangleAlertIcon} size="xs" className="text-status-warning" />
      <Text variant="body" className="flex-1 text-content-primary">
        {message}
      </Text>
      <Pressable
        testID="scout-report-translation-retry"
        onPress={onRetry}
        disabled={isRetrying}
        accessibilityLabel={retryLabel}
      >
        <Stack direction="horizontal" className="items-center gap-1">
          <Icon as={RotateCcwIcon} size="xs" className="text-content-primary" />
          <Text variant="body-strong" className="text-content-primary">
            {retryLabel}
          </Text>
        </Stack>
      </Pressable>
    </Stack>
  </Box>
);

TranslationUnavailableNoticeView.displayName =
  "TranslationUnavailableNoticeView";

export default memo(TranslationUnavailableNoticeView);
